import {
  type RentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete_Args,
  type RentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete_Options,
  useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type { ApiResponseErrorDto } from '@zg-rentals/ts-rental-manager-api-client';

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

const assertMultiplexAmenityDeleteMutationArgs = (
  args: RentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete_Args,
): void => {
  const { getParams } = args;

  if (typeof getParams !== 'object' || getParams === null) {
    throw new TypeError('useMultiplexAmenityDelete: `getParams` is required (DeleteParams).');
  }

  const sessionId = getParams.multiplexBuildingSessionId;
  const amenityId = getParams.amenityId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexAmenityDelete: `getParams.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (typeof amenityId !== 'string' || amenityId.trim() === '') {
    throw new TypeError(
      'useMultiplexAmenityDelete: `getParams.amenityId` is required on every call (non-empty string).',
    );
  }
};

export type UseMultiplexAmenityDeleteOptions = Omit<
  RentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexAmenityDeleteMutation = ReturnType<
  typeof useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete
>;

export type UseMultiplexAmenityDeleteResult = Omit<
  BaseMultiplexAmenityDeleteMutation,
  'mutate' | 'mutateAsync' | 'error'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete_Args,
    options?: Parameters<BaseMultiplexAmenityDeleteMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexAmenityDeleteMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete_Args,
    options?: Parameters<BaseMultiplexAmenityDeleteMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexAmenityDeleteMutation['mutateAsync']>;
  errors: ApiResponseErrorDto<unknown> | undefined;
};

/**
 * Multiplex additional-amenity delete: wraps POST `/api/web/v1/users/properties/multiplex/amenities/delete`.
 */
const useMultiplexAmenityDelete = (
  mutationOptions: UseMultiplexAmenityDeleteOptions = {},
): UseMultiplexAmenityDeleteResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_delete',
        details: 'useMultiplexAmenityDelete',
        ...metaOverrides?.context,
      },
    },
  });

  const { error, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexAmenityDeleteMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexAmenityDeleteMutationArgs(args);
      return mutateAsync(args, options);
    },
    errors: toApiResponseErrorDto(error),
  };
};

export default useMultiplexAmenityDelete;
